import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { PrimaryButton } from 'src/shared/components/button/Button';
import Alert from 'src/shared/components/ui/alert';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AdminViews } from 'src/routes/routesConfig';
import SiteManifestUploadDrawer from '../../components/site-manifest-upload-drawer/SiteManifestUploadDrawer';
import { CommitGate } from '../../components/site-manifest-preview/CommitGate';
import { CommitProgress } from '../../components/site-manifest-preview/CommitProgress';
import { ErrorRowsTable } from '../../components/site-manifest-preview/ErrorRowsTable';
import { PreviewSummaryCallout } from '../../components/site-manifest-preview/PreviewSummaryCallout';
import { UpdateRowsTable } from '../../components/site-manifest-preview/UpdateRowsTable';
import { useCommit } from '../../commit';
import {
    useGetSiteManifestSchemaQuery,
    type PreviewColumn,
    type PreviewRow,
    type PreviewSiteManifestResponse,
} from '../../state/api/organisationEngineSlice';

type SiteManifestPreviewProps = {
    currentCustomerId: string;
};

type LocationState = {
    preview?: PreviewSiteManifestResponse;
    /** Carried purely as a re-upload nonce — drives the commit-state reset
     *  effect. The FE no longer parses it; cells come from `preview.rows`. */
    csv?: string;
};

const SiteManifestPreview = ({ currentCustomerId }: SiteManifestPreviewProps) => {
    const location = useLocation();
    const state = (location.state ?? null) as LocationState | null;
    const preview = state?.preview;
    const csv = state?.csv ?? '';
    const [showReuploadDrawer, setShowReuploadDrawer] = useState(false);

    const commit = useCommit({
        bcId: currentCustomerId,
        preview: preview ?? { summary: { new: 0, update: 0, unchanged: 0, errors: 0, total: 0 }, rows: [] },
    });

    // Schema is org-independent and stable per session — RTK Query caches it
    // across renders so re-running the preview doesn't refetch.
    const { data: schema, isLoading: isSchemaLoading, isError: isSchemaError } = useGetSiteManifestSchemaQuery();
    const columns = schema?.columns ?? [];

    // Re-upload navigates to this same route with a fresh csv/preview in route
    // state — the page does NOT remount, so the commit hook's prior summary
    // and rowStates linger. Reset them whenever the csv changes so the user
    // lands on the new preview's pre-onboard view instead of the previous
    // run's failure banner.
    useEffect(() => {
        commit.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only on csv change
    }, [csv]);

    // Reached without uploading first (direct nav or refresh) — bounce back to
    // site management. The drawer is the only legitimate entry point.
    if (!preview) {
        return <Navigate to={navRoutesConfig[AdminViews.RISK_PORTFOLIO]({ customerId: currentCustomerId })} replace />;
    }

    // The schema feeds column headers + the failed-rows CSV download. Without
    // it the tables would render with no headers — don't show a half-broken
    // page; show the wait / error state instead.
    if (isSchemaLoading) {
        return (
            <div data-testid='manifest-schema-loading' className='p-6 text-sm text-grey-70'>
                Loading manifest schema…
            </div>
        );
    }
    if (isSchemaError || !schema) {
        return (
            <div className='p-6'>
                <Alert severity='error' data-testid='manifest-schema-error'>
                    Could not load the manifest schema. Please refresh, or return to{' '}
                    <Link
                        to={navRoutesConfig[AdminViews.RISK_PORTFOLIO]({ customerId: currentCustomerId })}
                        className='underline'
                        data-testid='back-to-site-management'
                    >
                        Site Management
                    </Link>
                    .
                </Alert>
            </div>
        );
    }

    const hasErrors = preview.summary.errors > 0;
    const showProgressView = commit.isCommitting || commit.summary !== null;

    return (
        <div data-testid='site-manifest-preview' className='flex flex-col gap-6 p-6'>
            <Link
                to={navRoutesConfig[AdminViews.RISK_PORTFOLIO]({ customerId: currentCustomerId })}
                className='text-sm text-grey-70 underline w-fit'
                data-testid='back-to-site-management'
            >
                ← Back to Site Management
            </Link>

            <div className='flex items-start justify-between gap-4'>
                <div>
                    <h1 className='text-xl font-semibold'>Manifest Preview</h1>
                    <p className='text-sm text-grey-70'>Review classifications and resolve any errors before onboarding.</p>
                </div>
                {hasErrors && !showProgressView && (
                    <PrimaryButton onClick={() => setShowReuploadDrawer(true)} data-testid='reupload-manifest-button'>
                        <span className='inline-flex items-center gap-2'>
                            <Upload className='w-4 h-4' /> Re-upload fixed CSV
                        </span>
                    </PrimaryButton>
                )}
            </div>

            <PreviewResult
                preview={preview}
                columns={columns}
                bcId={currentCustomerId}
                commit={commit}
                showProgressView={showProgressView}
                onReupload={() => setShowReuploadDrawer(true)}
            />

            <SiteManifestUploadDrawer
                open={showReuploadDrawer}
                onOpenChange={setShowReuploadDrawer}
                currentCustomerId={currentCustomerId}
            />
        </div>
    );
};

type PreviewResultProps = {
    preview: PreviewSiteManifestResponse;
    columns: PreviewColumn[];
    bcId: string;
    commit: ReturnType<typeof useCommit>;
    showProgressView: boolean;
    onReupload: () => void;
};

const PreviewResult = ({ preview, columns, bcId, commit, showProgressView, onReupload }: PreviewResultProps) => {
    const { summary, rows } = preview;
    const errorRows = rows.filter((row): row is Extract<PreviewRow, { status: 'error' }> => row.status === 'error');
    const updateRows = rows.filter((row): row is Extract<PreviewRow, { status: 'update' }> => row.status === 'update');

    const cleanRunReady = summary.errors === 0 && summary.total > 0;
    const hasCommittableRows = summary.new + summary.update > 0;

    return (
        <div className='flex flex-col gap-6'>
            <PreviewSummaryCallout summary={summary} />
            {summary.total === 0 && (
                <Alert severity='warning' data-testid='preview-empty'>
                    The CSV has no data rows.
                </Alert>
            )}
            {!showProgressView && cleanRunReady && (
                <CommitGate
                    onCommit={commit.commit}
                    isCommitting={commit.isCommitting}
                    hasCommittableRows={hasCommittableRows}
                    newCount={summary.new}
                    updateCount={summary.update}
                />
            )}
            {!showProgressView && errorRows.length > 0 && <ErrorRowsTable errorRows={errorRows} columns={columns} />}
            {!showProgressView && updateRows.length > 0 && <UpdateRowsTable updateRows={updateRows} columns={columns} />}
            {showProgressView && (
                <CommitProgress
                    rowStates={commit.rowStates}
                    summary={commit.summary}
                    preview={preview}
                    columns={columns}
                    isCommitting={commit.isCommitting}
                    onReupload={onReupload}
                    bcId={bcId}
                />
            )}
        </div>
    );
};

export default SiteManifestPreview;
